import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import AnalyticsService from '../services/analytics';
import { Product, OrderDetails } from '../interface/interfaces';
import { useAuth } from '../context/AuthContext';

export const useAnalytics = () => {
  const location = useLocation();
  const { isLoggedIn, token } = useAuth();

  // Initialize analytics on mount
  useEffect(() => {
    AnalyticsService.initializeDeviceInfo();
  }, []);

  // Track page views automatically
  useEffect(() => {
    const trackPageView = async () => {
      const pathname = location.pathname;
      const pageName = pathname === '/' ? 'Home' : pathname.substring(1).replace(/\//g, '_');
      
      await AnalyticsService.logPageView(pageName, {
        path: pathname,
        search: location.search,
        hash: location.hash,
      });
    };

    trackPageView();
  }, [location]);

  // Set user ID when logged in
  useEffect(() => {
    if (isLoggedIn() && token) {
      // Extract user ID from JWT token
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        AnalyticsService.setUserId(payload.sub || payload.user_id || 'unknown');
      } catch (error) {
        console.error('Error extracting user ID from token:', error);
      }
    }
  }, [isLoggedIn, token]);

  const trackViewItem = useCallback(async (item: Product) => {
    const value = typeof item.price_data.current_price === 'string' 
      ? parseFloat(item.price_data.current_price) 
      : item.price_data.current_price;
    
    await AnalyticsService.logViewItem(item, value);
  }, []);

  const trackAddToCart = useCallback(async (item: Product | OrderDetails) => {
    const value = 'price_data' in item 
      ? (typeof item.price_data.current_price === 'string' 
          ? parseFloat(item.price_data.current_price) 
          : item.price_data.current_price)
      : item.totalAmount;
    
    await AnalyticsService.logAddToCart(item, value);
  }, []);

  const trackRemoveFromCart = useCallback(async (item: Product | OrderDetails) => {
    const value = 'price_data' in item 
      ? (typeof item.price_data.current_price === 'string' 
          ? parseFloat(item.price_data.current_price) 
          : item.price_data.current_price)
      : item.totalAmount;
    
    await AnalyticsService.logRemoveFromCart(item, value);
  }, []);

  const trackViewCart = useCallback(async (items: (Product | OrderDetails)[]) => {
    await AnalyticsService.logViewCart(items);
  }, []);

  const trackBeginCheckout = useCallback(async (value: number, items: (Product | OrderDetails)[]) => {
    await AnalyticsService.logBeginCheckout(value, items);
  }, []);

  const trackPurchase = useCallback(async (
    transactionId: string,
    value: number,
    items: (Product | OrderDetails)[],
    currency?: string,
    affiliation?: string,
    tax?: number,
    shipping?: number,
    coupon?: string
  ) => {
    await AnalyticsService.logPurchase(
      transactionId,
      value,
      items,
      currency,
      affiliation,
      tax,
      shipping,
      coupon
    );
  }, []);

  const trackAddToWishlist = useCallback(async (item: Product) => {
    const value = typeof item.price_data.current_price === 'string' 
      ? parseFloat(item.price_data.current_price) 
      : item.price_data.current_price;
    
    await AnalyticsService.logAddToWishlist(item, value);
  }, []);

  return {
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackPurchase,
    trackAddToWishlist
  };
};
